import { useRef, useCallback, useEffect } from 'react';
import { useRealtime } from '../realtime/RealtimeContext';
import api from '../api/client';
import { ensurePush } from '../utils/push';

export const NOTIFY_KEY = 'kmt_notify_enabled';

// Map an audit entry's entity type to a filter category (null = general, always shown).
function categoryOf(e) {
  const et = (e.entity_type || '').toLowerCase();
  if (et === 'award') return 'awards';
  if (et === 'material') return 'stock';
  if (et === 'problem') return 'problems';
  if (et === 'job' || et === 'schedule') return 'schedule';
  return null;
}

// Shows a phone/desktop system notification for each new factory event (audit entry).
// Gated by the Settings toggle (localStorage), per-category filters + the browser permission.
export default function EventNotifier() {
  const lastTsRef = useRef(null);
  const seededRef = useRef(false);

  const check = useCallback(() => {
    const enabled = localStorage.getItem(NOTIFY_KEY) === '1';
    if (!enabled || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    api.listAudit().then((list) => {
      if (!Array.isArray(list) || list.length === 0) return;
      if (!seededRef.current) { lastTsRef.current = list[0].ts; seededRef.current = true; return; }
      const fresh = [];
      for (const e of list) {
        if (e.ts && e.ts > lastTsRef.current) fresh.push(e); else break;
      }
      if (fresh.length) {
        lastTsRef.current = fresh[0].ts;
        fresh.slice(0, 3).reverse().forEach((e) => {
          // Problems/awards/stock/jobs/schedule are delivered via Web Push (works when closed too);
          // the in-app notifier only covers the remaining general events to avoid duplicates.
          if (categoryOf(e) !== null) return;
          const body = e.detail || `${e.action || ''} ${e.entity_type || ''}`.trim();
          try {
            new Notification('KMT Factory · ' + (e.actor || 'Update'), { body, tag: e.id, icon: '/favicon.ico' });
          } catch (err) { /* some mobile browsers block direct construction */ }
        });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => { ensurePush(localStorage.getItem(NOTIFY_KEY) === '1'); }, []);
  useRealtime(check);
  return null;
}

